import { WithSkeleton } from '@capsicum/common';
import { CardBuilder, Image, Skeleton, SkeletonItem } from '@capsicum/common-web';
import { memo, useMemo } from 'react';
import styled from 'styled-components';

/**
 * CategoryCard is the FanShop card used to display all the product categories.
 *
 * @param {CategoryCardProps} props - The props of the card
 * @param {string} props.title The title of the card
 * @param {string} props.img The image to be displayed in the card
 * @param {() => void} props.onClick The callback function to be called on card click
 * @param {string} props.testID the ID to enable e2e framework to grab this element
 * @returns {JSX.Element} The category card
 */

export type CategoryCardProps = {
	blurHash: string;
	'data-tutorial'?: string;
	img: string;
	key?: string;
	maxWidth?: number;
	onClick?: () => void;
	style?: React.CSSProperties;
	testID: string;
	title: string;
};

const CategoryCardBaseComponent = ({
	blurHash,
	img,
	maxWidth = 368,
	onClick,
	style,
	testID,
	title,
	...props
}: CategoryCardProps) => {
	const image = useMemo(
		() =>
			img ? (
				<S.StyledImage
					alt={`${title} image`}
					blurhash={blurHash}
					height={160}
					optimized
					src={img}
					width={maxWidth}
				/>
			) : undefined,
		[blurHash, img, maxWidth, title]
	);

	const card = new CardBuilder()
		.setProps(props)
		.setCardStyle({
			...style,
			height: 214,
			maxWidth: maxWidth
		})
		.setProps({ 'data-tutorial': `${props['data-tutorial']}`, testID })
		.setTitle(title)
		.setMediaContent(image)
		.setOnActivate(() => onClick?.());

	return card.build();
};

export const CategoryCardBase = memo(CategoryCardBaseComponent);

export const CategoryCard = WithSkeleton(CategoryCardBase, p => (
	<Skeleton height={214} width={368} {...p}>
		<SkeletonItem />
	</Skeleton>
));

namespace S {
	export const StyledImage = styled(Image)`
		object-fit: cover;
	`;
}
